import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";

import { queryClient } from "../libs/query-client";
import { fetchUser } from "../services/user";
import { useAuthStore } from "../stores/auth";

type Props = {
  children: React.ReactNode;
};

/**
 * The TokenAuthProvider wrapper is responsible for handling external
 * authentication tokens passed via URL query parameters (e.g., from main dashboard).
 *
 * @param children The children to render.
 */
export const TokenAuthProvider = ({ children }: Props) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [hasCheckedToken, setHasCheckedToken] = useState(false);

  useEffect(() => {
    const authenticateWithToken = async () => {
      // Only check once per session
      if (hasCheckedToken) return;

      const token = new URLSearchParams(location.search).get("token");
      
      if (!token) {
        setHasCheckedToken(true);
        return;
      }

      setIsAuthenticating(true);

      try {
        // Call the bypass endpoint with the token
        const response = await fetch(`/api/auth/bypass?token=${token}`, {
          method: "GET",
          credentials: "include",
        });

        if (response.ok) {
          // Fetch the authenticated user
          const user = await queryClient.fetchQuery({
            queryKey: ["user"],
            queryFn: fetchUser,
          });

          if (user) {
            useAuthStore.setState({ user });
            
            // Remove token from URL to keep it clean
            const newUrl = new URL(window.location.href);
            newUrl.searchParams.delete("token");
            window.history.replaceState({}, "", newUrl.pathname + newUrl.search);
          }
        }
      } catch (error) {
        console.error("Token authentication failed:", error);
      } finally {
        setIsAuthenticating(false);
        setHasCheckedToken(true);
      }
    };

    void authenticateWithToken();
  }, [location.search, hasCheckedToken]);

  // Show loading state while authenticating with token
  if (isAuthenticating) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-blue-600"></div>
          <p className="text-gray-500">Authenticating...</p>
        </div>
      </div>
    );
  }

  return children;
};

